// DelEmployee.js
// Creating a class to house individual shift data
import { v4 as uuidv4 } from "uuid";

export class DelShift {
  constructor(employeeRowId, data = {}) {
    this.id = data.id || uuidv4();
    this.employeeRowId = employeeRowId;
    this.employeeId = data.employee_id || null;
    this.startTime = data.shift_start || null;
    this.endTime = data.shift_end || null;
    this.totalTime = data.total || null;
  }

  // Update the start time of a shift
  updateShiftStartTime(startTime) {
    this.startTime = startTime;
    this.totalTime = this.updateShiftTotalTime();
  }

  // Update the end time of a shift
  updateShiftEndTime(endTime) {
    this.endTime = endTime;
    this.totalTime = this.updateShiftTotalTime();
  }

  // Update the total time of a shift
  updateShiftTotalTime() {
    if (!this.startTime || !this.endTime) {
      this.totalTime = null;
      return;
    }
    const getMinutes = (time) => {
      const [hours, minutes] = time.split(":").map(Number);
      return hours * 60 + minutes;
    };

    let startTime = getMinutes(this.startTime);
    let endTime = getMinutes(this.endTime);

    // If the end time is less than the start time, then we need to add 24 hours to the end time
    if (endTime < startTime) {
      endTime += 24 * 60;
    }

    return Math.round(((endTime - startTime) / 60) * 100) / 100;
  }

  // Setter methods for broadcast compatibility
  // applyPatchToObject uses these to bridge DB column names to model properties

  // Maps DB column shift_start to model property startTime
  setShiftStart(value) {
    this.startTime = value;
    this.totalTime = this.updateShiftTotalTime();
  }

  // Maps DB column shift_end to model property endTime
  setShiftEnd(value) {
    this.endTime = value;
    this.totalTime = this.updateShiftTotalTime();
  }

  // Maps DB column total to model property totalTime
  setTotal(value) {
    this.totalTime = value;
  }

  // To json for sending to the database
  toJson() {
    return {
      id: this.id,
      employee_id: this.employeeId,
      shift_start: this.startTime,
      shift_end: this.endTime,
      total: this.totalTime,
    };
  }

  toPatch(action, fields) {
    const fieldMap = {
      shiftId: "id",
      startTime: "shift_start",
      endTime: "shift_end",
      totalTime: "total",
    };
    const patch = {
      patch_type: "shift",
      action: action,
      employee_id: this.employeeId,
      id: this.id,
    };
    for (const [key, value] of Object.entries(fields)) {
      const dbField = fieldMap[key];
      if (dbField) {
        patch[dbField] = value;
      }
    }
    return patch;
  }
}

// Creating a class to house individual employee data, along with shift data
// Using a rowId to identify the employee information in the row, since the employeeId is not unique can change.
export class DelEmployee {
  constructor(data = {}) {
    // Unique ID for each employee row
    this.employeeRowId = data.id || uuidv4();
    this.employeeId = data.employee_id || null;
    this.employeeName = data.employee_name
      ? data.employee_id + " - " + data.employee_name
      : null;
    this.positionId = data.position_cat_id || null;
    // Update the positionName based on the positionID and the employee_positions table
    this.positionName = data.employee_position || data.position_name || null;
    // Per diem tracking - boolean from backend
    this.perDiem = data.per_diem || false;
    // This will be an array of DelShift objects
    this.shifts = data.shifts
      ? data.shifts.map((shift) => {
          return new DelShift(this.employeeRowId, shift);
        })
      : [new DelShift(this.employeeRowId)];
  }

  // This is strange, but it is the only way to delete a shift from the employee.
  deleteShift(shiftId) {
    this.shifts = this.shifts.filter((shift) => shift.id !== shiftId);
  }

  // Adding a shift to the employee, basically adding a new DelShift object to the shifts array.
  addNewShift(employeeRowId) {
    const shiftData = {
      employee_id: this.employeeId,
    };
    const newShift = new DelShift(employeeRowId, shiftData);
    this.shifts.push(newShift);
    return newShift.id;
  }

  // Updating the name of an employee
  selectEmployee(employeeId, employeeName) {
    this.employeeId = employeeId;
    this.employeeName = employeeName;
    // Going throught the shifts and updating the employeeId and employeeName
    this.shifts.forEach((shift) => {
      shift.employeeId = employeeId;
    });
  }

  // Setter methods for broadcast compatibility
  // applyPatchToObject uses these to bridge DB column names to model properties

  // Formats employee name as "ID - Name" to match dropdown label format
  setEmployeeName(value) {
    if (value && this.employeeId) {
      this.employeeName = `${this.employeeId} - ${value}`;
    } else {
      this.employeeName = value;
    }
  }

  // Maps DB column position_cat_id to model property positionId
  setPositionCatId(value) {
    this.positionId = value;
  }

  // Maps DB column employee_position to model property positionName
  setEmployeePosition(value) {
    this.positionName = value;
  }

  // Maps DB column per_diem to model property perDiem
  setPerDiem(value) {
    this.perDiem = value;
  }

  // Updating the employee position name for Dels coming in from the database
  updateEmployeePositionName(employeePositionList) {
    const positionName = employeePositionList.find(
      (position) => position.cat_id === this.positionId
    )?.label;
    this.positionName = positionName ? positionName : this.positionName;
  }

  // Selecting an employee position

  selectEmployeePosition(positionId, positionName) {
    this.positionId = positionId;
    this.positionName = positionName;
  }

  // To json for sending to the database
  toJson() {
    return {
      id: this.employeeRowId,
      employee_id: this.employeeId,
      employee_position: this.positionName,
      position_cat_id: this.positionId,
      per_diem: this.perDiem,  // Send as boolean, backend converts to 0/1
      shifts: this.shifts.map((shift) => shift.toJson()),
    };
  }

  toPatch(action, fields) {
    const fieldMap = {
      employeeRowId: "id",
      employeeId: "employee_id",
      positionId: "position_cat_id",
      positionName: "employee_position",
      perDiem: "per_diem",  // Map perDiem to per_diem for patches
    };
    const patch = {
      patch_type: "employee",
      action: action,
      id: this.employeeRowId,
    };
    for (const [key, value] of Object.entries(fields)) {
      const dbField = fieldMap[key];
      if (dbField) {
        patch[dbField] = value;
      }
    }
    return patch;
  }
}
